import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, RefreshCw, Loader2, CheckCircle2, Clock, Circle, Ban, Pencil, X, Check, ShieldCheck, ChevronDown, ChevronRight, ChevronUp, Bold, Italic, List, ListOrdered, MessageSquare, AlertTriangle, Send, ImagePlus, Images, CalendarDays, Table } from "lucide-react";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, parseISO } from "date-fns";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Pagination } from "@/components/Pagination";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const priorityConfig = {
  P0: { badge: "bg-red-50 text-red-700 border-red-200", label: "Critical" },
  P1: { badge: "bg-amber-50 text-amber-700 border-amber-200", label: "High" },
  P2: { badge: "bg-blue-50 text-blue-700 border-blue-200", label: "Medium" },
  P3: { badge: "bg-slate-100 text-slate-600 border-slate-200", label: "Low" },
};

const statusConfig = {
  "Not Started": { badge: "bg-slate-100 text-slate-600 border-slate-200", icon: Circle, dot: "bg-slate-400" },
  "In Progress": { badge: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock, dot: "bg-amber-500" },
  Completed: { badge: "bg-green-50 text-green-700 border-green-200", icon: CheckCircle2, dot: "bg-green-500" },
  Blocked: { badge: "bg-red-50 text-red-700 border-red-200", icon: Ban, dot: "bg-red-500" },
};

const statusCycle = {
  "Not Started": "In Progress",
  "In Progress": "Completed",
  Completed: "Blocked",
  Blocked: "Not Started",
};

export const RequirementsPanel = ({ onNavigate, highlightTarget }) => {
  const { selectedProduct } = useAuth();
  const productId = selectedProduct?.id;
  const [requirements, setRequirements] = useState([]);
  const [reqPage, setReqPage] = useState(1);
  const REQ_PAGE_SIZE = 10;
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    priority: "P1",
    assigned_to: [],
    due_date: "",
    pain_point_id: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterPriority, setFilterPriority] = useState(null);
  const [stakeholders, setStakeholders] = useState([]);
  const [expandedIds, setExpandedIds] = useState({});
  const [comments, setComments] = useState({});
  const [showComments, setShowComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [commentAuthor, setCommentAuthor] = useState({});
  const [loadingComments, setLoadingComments] = useState({});
  const [uploadingImage, setUploadingImage] = useState({});
  const [showImages, setShowImages] = useState({});
  const editDescRefs = useRef({});
  const newDescRef = useRef(null);
  const imageInputRefs = useRef({});
  const cardRefs = useRef({});

  const [painPoints, setPainPoints] = useState([]);
  const [trackingItems, setTrackingItems] = useState([]);

  const toggleExpand = (id) => setExpandedIds((p) => ({ ...p, [id]: !p[id] }));
  const exec = (id, cmd, val) => { document.execCommand(cmd, false, val); editDescRefs.current[id]?.focus(); };
  const execNew = (cmd, val) => { document.execCommand(cmd, false, val); newDescRef.current?.focus(); };

  const buildTableHTML = (rows, cols) => {
    let html = '<table style="border-collapse:collapse;width:100%;margin:8px 0"><thead><tr>';
    for (let c = 0; c < cols; c++) html += '<th style="border:1px solid #cbd5e1;padding:6px 10px;background:#f1f5f9;text-align:left;font-size:13px;font-weight:600">Header</th>';
    html += '</tr></thead><tbody>';
    for (let r = 0; r < rows - 1; r++) {
      html += '<tr>';
      for (let c = 0; c < cols; c++) html += '<td style="border:1px solid #cbd5e1;padding:6px 10px;font-size:13px">&nbsp;</td>';
      html += '</tr>';
    }
    html += '</tbody></table><p><br></p>';
    return html;
  };

  const insertTable = (id, rows, cols) => {
    const el = id ? editDescRefs.current[id] : newDescRef.current;
    if (el) { el.focus(); document.execCommand("insertHTML", false, buildTableHTML(rows, cols)); }
  };

  // Auto-scroll to highlighted requirement
  useEffect(() => {
    if (highlightTarget && cardRefs.current[highlightTarget]) {
      cardRefs.current[highlightTarget].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightTarget, requirements]);

  const fetchRequirements = useCallback(async () => {
    try {
      const params = productId ? { product_id: productId } : {};
      const res = await axios.get(`${API}/requirements`, { params });
      setRequirements(res.data);
    } catch (err) {
      console.error("Failed to fetch requirements:", err);
    }
    setLoading(false);
  }, [productId]);

  useEffect(() => {
    fetchRequirements();
    const params = productId ? { product_id: productId } : {};
    axios.get(`${API}/stakeholders`, { params }).then((res) => setStakeholders(res.data)).catch(() => {});
    axios.get(`${API}/painpoints`, { params }).then((res) => setPainPoints(res.data)).catch(() => {});
    axios.get(`${API}/tracking`, { params }).then((res) => setTrackingItems(res.data)).catch(() => {});
  }, [fetchRequirements, productId]);

  const addRequirement = async () => {
    if (!formData.title.trim()) return;
    setSubmitting(true);
    try {
      const descHtml = newDescRef.current?.innerHTML || "";
      const res = await axios.post(`${API}/requirements`, { ...formData, description: descHtml, product_id: productId });
      setRequirements((prev) => [...prev, res.data]);
      setFormData({ title: "", description: "", priority: "P1", assigned_to: [], due_date: "", pain_point_id: "" });
      if (newDescRef.current) newDescRef.current.innerHTML = "";
      setShowForm(false);
    } catch (err) {
      console.error("Failed to add requirement:", err);
    }
    setSubmitting(false);
  };

  const cycleStatus = async (req) => {
    const next = statusCycle[req.status];
    try {
      await axios.patch(`${API}/requirements/${req.id}`, { status: next });
      setRequirements((prev) => prev.map((r) => (r.id === req.id ? { ...r, status: next } : r)));
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  const saveEdit = async (req) => {
    try {
      const descHtml = editDescRefs.current[req.id]?.innerHTML ?? editData.description ?? req.description;
      const res = await axios.patch(`${API}/requirements/${req.id}`, { ...editData, description: descHtml });
      setRequirements((prev) => prev.map((r) => (r.id === req.id ? res.data : r)));
      setEditingId(null);
    } catch (err) {
      console.error("Failed to save:", err);
    }
  };

  const deleteRequirement = async (id) => {
    try {
      await axios.delete(`${API}/requirements/${id}`);
      setRequirements((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      console.error("Failed to delete:", err);
    }
  };

  const toggleApproval = async (req, approved, approvedBy = "") => {
    const payload = approved
      ? { approved: true, approved_by: approvedBy, approved_at: new Date().toISOString() }
      : { approved: false, approved_by: "", approved_at: "" };
    try {
      const res = await axios.patch(`${API}/requirements/${req.id}`, payload);
      setRequirements((prev) => prev.map((r) => (r.id === req.id ? res.data : r)));
    } catch (err) {
      console.error("Failed to update approval:", err);
    }
  };

  // Comments Functions
  const fetchComments = async (reqId) => {
    setLoadingComments((p) => ({ ...p, [reqId]: true }));
    try {
      const res = await axios.get(`${API}/requirements/${reqId}/comments`);
      setComments((p) => ({ ...p, [reqId]: res.data }));
    } catch (err) {
      console.error("Failed to fetch comments:", err);
    }
    setLoadingComments((p) => ({ ...p, [reqId]: false }));
  };

  const toggleComments = async (reqId) => {
    const newState = !showComments[reqId];
    setShowComments((p) => ({ ...p, [reqId]: newState }));
    if (newState && !comments[reqId]) {
      await fetchComments(reqId);
    }
  };

  const addComment = async (reqId) => {
    const text = newComment[reqId]?.trim();
    const author = commentAuthor[reqId]?.trim() || "Anonymous";
    if (!text) return;
    try {
      const res = await axios.post(`${API}/requirements/${reqId}/comments`, {
        author_name: author,
        comment: text,
      });
      setComments((p) => ({ ...p, [reqId]: [...(p[reqId] || []), res.data] }));
      setNewComment((p) => ({ ...p, [reqId]: "" }));
    } catch (err) {
      console.error("Failed to add comment:", err);
    }
  };

  const deleteComment = async (reqId, commentId) => {
    try {
      await axios.delete(`${API}/requirements/comments/${commentId}`);
      setComments((p) => ({
        ...p,
        [reqId]: (p[reqId] || []).filter((c) => c.id !== commentId),
      }));
    } catch (err) {
      console.error("Failed to delete comment:", err);
    }
  };

  const updateBlockedReason = async (req, reason) => {
    try {
      await axios.patch(`${API}/requirements/${req.id}`, { blocked_reason: reason });
      setRequirements((prev) => prev.map((r) => (r.id === req.id ? { ...r, blocked_reason: reason } : r)));
    } catch (err) {
      console.error("Failed to update blocked reason:", err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  // Image upload functions
  const uploadImage = async (reqId, file) => {
    if (!file) return;
    setUploadingImage((p) => ({ ...p, [reqId]: true }));
    try {
      // Get signature - use 'auto' resource type to support images and PDFs
      const sigRes = await axios.get(`${API}/cloudinary/signature?folder=requirements&resource_type=auto`);
      const sig = sigRes.data;
      // Upload to Cloudinary with auto resource type
      const formData = new FormData();
      formData.append("file", file);
      formData.append("api_key", sig.api_key);
      formData.append("timestamp", sig.timestamp);
      formData.append("signature", sig.signature);
      formData.append("folder", sig.folder);
      formData.append("resource_type", "auto");
      
      const cloudRes = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/auto/upload`, { method: "POST", body: formData });
      const cloudData = await cloudRes.json();
      console.log("Cloudinary response:", cloudData);
      
      if (cloudData.secure_url) {
        const req = requirements.find((r) => r.id === reqId);
        const newImages = [...(req.images || []), { 
          url: cloudData.secure_url, 
          public_id: cloudData.public_id, 
          uploaded_at: new Date().toISOString(),
          format: cloudData.format,
          resource_type: cloudData.resource_type
        }];
        await axios.patch(`${API}/requirements/${reqId}`, { images: newImages });
        setRequirements((prev) => prev.map((r) => r.id === reqId ? { ...r, images: newImages } : r));
        // Auto-expand images section after upload
        setShowImages((p) => ({ ...p, [reqId]: true }));
      } else {
        console.error("Cloudinary error:", cloudData);
        alert("Upload failed: " + (cloudData.error?.message || "Unknown error"));
      }
    } catch (err) {
      console.error("Image upload failed:", err);
      alert("Image upload failed: " + err.message);
    }
    setUploadingImage((p) => ({ ...p, [reqId]: false }));
    if (imageInputRefs.current[reqId]) imageInputRefs.current[reqId].value = "";
  };

  const deleteImage = async (reqId, imageIndex) => {
    const req = requirements.find((r) => r.id === reqId);
    const newImages = (req.images || []).filter((_, i) => i !== imageIndex);
    try {
      await axios.patch(`${API}/requirements/${reqId}`, { images: newImages });
      setRequirements((prev) => prev.map((r) => r.id === reqId ? { ...r, images: newImages } : r));
    } catch (err) {
      console.error("Failed to delete image:", err);
    }
  };

  const toggleImages = (reqId) => setShowImages((p) => ({ ...p, [reqId]: !p[reqId] }));

  // Reorder requirements
  const moveRequirement = async (reqId, direction) => {
    try {
      await axios.post(`${API}/requirements/reorder`, { id: reqId, direction });
      fetchRequirements();
    } catch (err) {
      console.error("Failed to reorder:", err);
    }
  };

  const filtered = requirements.filter((r) => {
    if (filterStatus && r.status !== filterStatus) return false;
    if (filterPriority && r.priority !== filterPriority) return false;
    return true;
  });

  const counts = {
    total: requirements.length,
    "Not Started": requirements.filter((r) => r.status === "Not Started").length,
    "In Progress": requirements.filter((r) => r.status === "In Progress").length,
    Completed: requirements.filter((r) => r.status === "Completed").length,
    Blocked: requirements.filter((r) => r.status === "Blocked").length,
  };

  return (
    <>
    <div data-testid="requirements-panel">
      <p className="text-xs font-semibold tracking-widest uppercase text-[#A78BFA] mb-3">
        Tracking
      </p>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 dark:text-slate-100 font-['Outfit']">
        Requirements
      </h1>
      <p className="text-base text-slate-500 dark:text-slate-400 mt-2">
        Track implementation requirements with priority and status
      </p>

      <Separator className="my-8 bg-slate-200 dark:bg-slate-700" />

      {/* Counters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="req-counters">
        <Badge className="bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-100">
          {counts.total} Total
        </Badge>
        {["Not Started", "In Progress", "Completed", "Blocked"].map((s) => {
          const cfg = statusConfig[s];
          return (
            <Badge key={s} className={`${cfg.badge} border hover:opacity-90`}>
              <span className={`w-2 h-2 rounded-full ${cfg.dot} mr-1.5 inline-block`} />
              {counts[s]} {s}
            </Badge>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-6" data-testid="req-filters">
        <span className="text-xs font-medium text-slate-500">Filter:</span>
        <select
          value={filterStatus || ""}
          onChange={(e) => setFilterStatus(e.target.value || null)}
          className="h-8 px-3 rounded-lg text-xs font-semibold border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400 cursor-pointer"
          data-testid="req-filter-status"
        >
          <option value="">All Statuses</option>
          <option value="Not Started">Not Started</option>
          <option value="In Progress">In Progress</option>
          <option value="Completed">Completed</option>
          <option value="Blocked">Blocked</option>
        </select>
        <select
          value={filterPriority || ""}
          onChange={(e) => setFilterPriority(e.target.value || null)}
          className="h-8 px-3 rounded-lg text-xs font-semibold border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400 cursor-pointer"
          data-testid="req-filter-priority"
        >
          <option value="">All Priorities</option>
          <option value="P0">P0 - Critical</option>
          <option value="P1">P1 - High</option>
          <option value="P2">P2 - Medium</option>
          <option value="P3">P3 - Low</option>
        </select>
        {(filterStatus || filterPriority) && (
          <button
            onClick={() => { setFilterStatus(null); setFilterPriority(null); }}
            className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-purple-600 text-white hover:bg-purple-700 transition-colors"
            data-testid="req-filter-clear"
          >
            Clear
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <>
        <div className="flex flex-col gap-3" data-testid="requirements-list">
          {filtered.slice((reqPage - 1) * REQ_PAGE_SIZE, reqPage * REQ_PAGE_SIZE).map((req, visIdx) => {
            const sCfg = statusConfig[req.status] || statusConfig["Not Started"];
            const pCfg = priorityConfig[req.priority] || priorityConfig.P1;
            const StatusIcon = sCfg.icon;
            const isEditing = editingId === req.id;
            const displayNum = req.req_number || ((reqPage - 1) * REQ_PAGE_SIZE + visIdx + 1);
            const positionNum = (reqPage - 1) * REQ_PAGE_SIZE + visIdx + 1;

            return (
              <Card
                key={req.id}
                ref={(el) => { cardRefs.current[req.id] = el; }}
                className={`card-modern overflow-hidden transition-all duration-500 ${
                  req.status === "Completed" ? "opacity-75" : ""
                } ${highlightTarget === req.id ? "ring-2 ring-violet-500 dark:ring-violet-400 shadow-lg shadow-violet-500/10" : ""}`}
                data-testid={`requirement-card-${req.id}`}
              >
                <CardContent className="p-0">
                  {isEditing ? (
                    <div className="p-5 space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="text-purple-600 font-mono font-semibold">{String(positionNum).padStart(3, '0')}.</span>
                        <span className="text-[10px] font-bold text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-800 px-1.5 py-0.5 rounded font-mono">REQ-{String(displayNum).padStart(3, '0')}</span>
                        <Input
                          value={editData.title ?? req.title}
                          onChange={(e) => setEditData((p) => ({ ...p, title: e.target.value }))}
                          className="font-semibold flex-1"
                          data-testid={`req-edit-title-${req.id}`}
                        />
                      </div>
                      {/* Rich text toolbar */}
                      <div className="flex items-center gap-1 border border-slate-200 rounded-lg px-2 py-1 bg-slate-50" data-testid={`req-edit-toolbar-${req.id}`}>
                        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec(req.id, "bold"); }} className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Bold"><Bold className="w-4 h-4" /></button>
                        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec(req.id, "italic"); }} className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Italic"><Italic className="w-4 h-4" /></button>
                        <div className="w-px h-5 bg-slate-200 mx-1" />
                        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec(req.id, "insertUnorderedList"); }} className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Bullets"><List className="w-4 h-4" /></button>
                        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec(req.id, "insertOrderedList"); }} className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Numbers"><ListOrdered className="w-4 h-4" /></button>
                        <div className="w-px h-5 bg-slate-200 mx-1" />
                        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec(req.id, "fontSize", "3"); }} className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Small"><span className="text-[10px] font-bold">A</span></button>
                        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec(req.id, "fontSize", "4"); }} className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Normal"><span className="text-xs font-bold">A</span></button>
                        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec(req.id, "fontSize", "5"); }} className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Large"><span className="text-sm font-bold">A</span></button>
                        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec(req.id, "fontSize", "6"); }} className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Extra Large"><span className="text-base font-bold">A</span></button>
                        <div className="w-px h-5 bg-slate-200 mx-1" />
                        <Popover>
                          <PopoverTrigger asChild>
                            <button type="button" className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Insert Table" data-testid={`req-insert-table-${req.id}`}><Table className="w-4 h-4" /></button>
                          </PopoverTrigger>
                          <PopoverContent className="w-48 p-3" align="start">
                            <p className="text-xs font-semibold text-slate-600 mb-2">Insert Table</p>
                            <div className="flex items-center gap-2 mb-2">
                              <label className="text-[10px] text-slate-500 w-10">Rows</label>
                              <Input type="number" min="2" max="20" defaultValue="3" className="h-7 text-xs" id={`tbl-rows-${req.id}`} />
                            </div>
                            <div className="flex items-center gap-2 mb-3">
                              <label className="text-[10px] text-slate-500 w-10">Cols</label>
                              <Input type="number" min="1" max="10" defaultValue="3" className="h-7 text-xs" id={`tbl-cols-${req.id}`} />
                            </div>
                            <Button size="sm" className="w-full h-7 text-xs bg-[#0F172A] hover:bg-slate-800 text-white" onClick={() => { const r = parseInt(document.getElementById(`tbl-rows-${req.id}`).value) || 3; const c = parseInt(document.getElementById(`tbl-cols-${req.id}`).value) || 3; insertTable(req.id, r, c); }}>Insert</Button>
                          </PopoverContent>
                        </Popover>
                      </div>
                      {/* Content editable description */}
                      <div
                        ref={(el) => { editDescRefs.current[req.id] = el; if (el && !el.dataset.init) { el.innerHTML = req.description || ""; el.dataset.init = "1"; } }}
                        contentEditable
                        suppressContentEditableWarning
                        data-testid={`req-edit-desc-${req.id}`}
                        className="min-h-[120px] p-3 border border-slate-200 rounded-lg text-sm text-slate-700 leading-relaxed focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400 prose prose-sm max-w-none
                          [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2
                          [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2
                          [&_li]:my-0.5
                          [&_b]:font-bold [&_strong]:font-bold
                          [&_i]:italic [&_em]:italic
                          [&_table]:w-full [&_table]:border-collapse [&_table]:my-2
                          [&_th]:border [&_th]:border-slate-300 [&_th]:bg-slate-100 [&_th]:px-2.5 [&_th]:py-1.5 [&_th]:text-left [&_th]:text-xs [&_th]:font-semibold
                          [&_td]:border [&_td]:border-slate-300 [&_td]:px-2.5 [&_td]:py-1.5 [&_td]:text-xs"
                      />
                      <div className="flex items-center gap-3">
                        <Select
                          value={editData.priority ?? req.priority}
                          onValueChange={(val) => setEditData((p) => ({ ...p, priority: val }))}
                        >
                          <SelectTrigger className="w-32 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="P0">P0 - Critical</SelectItem>
                            <SelectItem value="P1">P1 - High</SelectItem>
                            <SelectItem value="P2">P2 - Medium</SelectItem>
                            <SelectItem value="P3">P3 - Low</SelectItem>
                          </SelectContent>
                        </Select>
                        <select
                          value=""
                          onChange={(e) => {
                            const val = e.target.value;
                            if (!val) return;
                            const current = editData.assigned_to ?? (Array.isArray(req.assigned_to) ? req.assigned_to : req.assigned_to ? [req.assigned_to] : []);
                            if (!current.includes(val)) {
                              setEditData((p) => ({ ...p, assigned_to: [...current, val] }));
                            }
                          }}
                          className="h-8 px-2 rounded-md text-xs font-medium border border-slate-200 bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400 cursor-pointer w-48"
                          data-testid={`req-edit-assigned-${req.id}`}
                        >
                          <option value="">Add assignee...</option>
                          {stakeholders.filter(s => {
                            const current = editData.assigned_to ?? (Array.isArray(req.assigned_to) ? req.assigned_to : req.assigned_to ? [req.assigned_to] : []);
                            return !current.includes(s.name);
                          }).map((s) => (
                            <option key={s.id} value={s.name}>{s.name}</option>
                          ))}
                        </select>
                        {/* Assigned chips */}
                        <div className="flex items-center gap-1 flex-wrap">
                          {(editData.assigned_to ?? (Array.isArray(req.assigned_to) ? req.assigned_to : req.assigned_to ? [req.assigned_to] : [])).map((name, i) => (
                            <span key={i} className="inline-flex items-center gap-1 bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full text-[11px] font-medium border border-purple-200">
                              {name}
                              <button
                                onClick={() => {
                                  const current = editData.assigned_to ?? (Array.isArray(req.assigned_to) ? req.assigned_to : req.assigned_to ? [req.assigned_to] : []);
                                  setEditData((p) => ({ ...p, assigned_to: current.filter(n => n !== name) }));
                                }}
                                className="hover:text-red-500 transition-colors"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                        {/* Due Date Picker */}
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              className="h-8 px-3 rounded-md text-xs font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 flex items-center gap-2"
                              data-testid={`req-edit-duedate-${req.id}`}
                            >
                              <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                              {(editData.due_date ?? req.due_date) ? 
                                (() => { try { return "Due: " + format(parseISO(editData.due_date ?? req.due_date), "MMM d, yyyy"); } catch { return editData.due_date ?? req.due_date; } })()
                                : "Set due date"}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={(() => { try { return (editData.due_date ?? req.due_date) ? parseISO(editData.due_date ?? req.due_date) : undefined; } catch { return undefined; } })()}
                              onSelect={(date) => setEditData((p) => ({ ...p, due_date: date ? format(date, "yyyy-MM-dd") : "" }))}
                            />
                          </PopoverContent>
                        </Popover>
                        {/* Pain Point Link */}
                        <Select value={editData.pain_point_id ?? req.pain_point_id ?? "_none"} onValueChange={(v) => setEditData((p) => ({ ...p, pain_point_id: v === "_none" ? "" : v }))}>
                          <SelectTrigger className="h-8 w-[80px] text-xs border-slate-200 bg-white text-slate-600" data-testid={`req-edit-painpoint-${req.id}`}>
                            <SelectValue placeholder="Pain Point" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">--</SelectItem>
                            {painPoints.map((pp, i) => (
                              <SelectItem key={pp.id} value={pp.id}>PP-{String(pp.pp_number || (i + 1)).padStart(3, '0')}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="flex items-center gap-1 ml-auto">
                          <Button size="sm" onClick={() => saveEdit(req)} className="h-7 bg-green-600 hover:bg-green-700 text-white text-xs">
                            <Check className="w-3.5 h-3.5 mr-1" /> Save
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="h-7 text-xs">
                            <X className="w-3.5 h-3.5 mr-1" /> Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Clickable header row */}
                      <div
                        className="px-5 pt-4 pb-3 flex items-start justify-between gap-4 cursor-pointer select-none hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition-colors"
                        onClick={() => toggleExpand(req.id)}
                        data-testid={`req-toggle-${req.id}`}
                      >
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="flex flex-col -mr-1" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => moveRequirement(req.id, "up")}
                              className="p-0.5 rounded hover:bg-slate-100 text-slate-300 hover:text-slate-600 transition-colors"
                              title="Move Up"
                              data-testid={`req-move-up-${req.id}`}
                            >
                              <ChevronUp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => moveRequirement(req.id, "down")}
                              className="p-0.5 rounded hover:bg-slate-100 text-slate-300 hover:text-slate-600 transition-colors"
                              title="Move Down"
                              data-testid={`req-move-down-${req.id}`}
                            >
                              <ChevronDown className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <div className="number-badge">{String(positionNum).padStart(3, '0')}</div>
                          <span className="text-[10px] font-bold text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-800 px-1.5 py-0.5 rounded font-mono">REQ-{String(displayNum).padStart(3, '0')}</span>
                          <h3 className={`text-base font-semibold font-['Outfit'] ${
                            req.status === "Completed" ? "text-slate-400 line-through" : "text-slate-800"
                          }`}>
                            {req.title}
                          </h3>
                          <Badge className={`${pCfg.badge} border text-[11px] font-semibold shadow-sm`}>
                            {req.priority}
                          </Badge>
                          {req.assigned_to && (Array.isArray(req.assigned_to) ? req.assigned_to.length > 0 : !!req.assigned_to) && (
                            <span className="text-[11px] text-slate-500 font-medium bg-slate-100 px-2 py-0.5 rounded-md">
                              {Array.isArray(req.assigned_to) ? req.assigned_to.join(", ") : req.assigned_to}
                            </span>
                          )}
                          {req.due_date && (
                            <span className="text-[11px] text-slate-500 font-medium flex items-center gap-1 bg-slate-100 px-2 py-0.5 rounded-md">
                              <CalendarDays className="w-3 h-3" />
                              {(() => { try { return "Due: " + format(parseISO(req.due_date), "MMM d"); } catch { return req.due_date; } })()}
                            </span>
                          )}
                          {req.pain_point_id && (() => {
                            const pp = painPoints.find(pp => pp.id === req.pain_point_id);
                            if (!pp) return null;
                            return (
                              <span className="text-[11px] text-amber-700 font-semibold flex items-center gap-1 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200">
                                PP-{String(pp.pp_number || (painPoints.indexOf(pp) + 1)).padStart(3, '0')}
                              </span>
                            );
                          })()}
                          {trackingItems.filter((t) => t.requirement_id === req.id).map((t) => (
                            <button
                              key={t.id}
                              onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate("tracking", t.id); }}
                              className="inline-flex items-center"
                              title={`Go to ${t.user_story_title || "User Story"}`}
                              data-testid={`req-goto-story-${t.id}`}
                            >
                              <span className="text-[11px] text-violet-600 dark:text-violet-400 font-semibold flex items-center gap-1 bg-violet-50 dark:bg-violet-900/30 px-2 py-0.5 rounded-md border border-violet-200 dark:border-violet-800 hover:bg-violet-100 dark:hover:bg-violet-900/50 cursor-pointer transition-colors">
                                US-{String(t.story_number).padStart(3, '0')}
                              </span>
                            </button>
                          ))}
                          {req.approved && (
                            <Badge className="bg-gradient-to-r from-green-500 to-green-600 text-white border-0 text-[10px] shadow-sm">
                              <ShieldCheck className="w-3 h-3 mr-1" /> Approved
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => cycleStatus(req)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${sCfg.badge} border flex items-center gap-1.5 hover:opacity-80 transition-all shadow-sm`}
                            data-testid={`req-status-${req.id}`}
                            title="Click to cycle status"
                          >
                            <RefreshCw className="w-3 h-3" />
                            {req.status}
                          </button>
                        </div>
                      </div>

                      {/* Expandable body */}
                      {expandedIds[req.id] && (
                        <div className="px-5 pb-4">
                          {req.description && (
                            <div className="text-sm text-slate-500 leading-relaxed mb-3 prose prose-sm max-w-none pl-6
                              [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2
                              [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2
                              [&_li]:my-0.5
                              [&_b]:font-bold [&_strong]:font-bold
                              [&_i]:italic [&_em]:italic
                              [&_table]:w-full [&_table]:border-collapse [&_table]:my-2
                              [&_th]:border [&_th]:border-slate-300 [&_th]:bg-slate-100 [&_th]:px-2.5 [&_th]:py-1.5 [&_th]:text-left [&_th]:text-xs [&_th]:font-semibold
                              [&_td]:border [&_td]:border-slate-300 [&_td]:px-2.5 [&_td]:py-1.5 [&_td]:text-xs"
                              dangerouslySetInnerHTML={{ __html: req.description }}
                              data-testid={`req-desc-${req.id}`}
                            />
                          )}
                          <div className="flex items-center gap-2 pl-6">
                            <button
                              onClick={() => { setEditingId(req.id); setEditData({}); setExpandedIds((p) => ({ ...p, [req.id]: true })); }}
                              className="p-1.5 rounded hover:bg-slate-100 text-slate-300 hover:text-slate-600 transition-colors"
                              title="Edit"
                              data-testid={`req-edit-${req.id}`}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => setDeleteTarget({ id: req.id, label: req.title })}
                              className="p-1.5 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors"
                              title="Delete"
                              data-testid={`req-delete-${req.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                            <span className="text-[11px] text-slate-300 ml-auto">
                              {new Date(req.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                            </span>
                          </div>

                          {/* Approval */}
                          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-3 flex-wrap pl-6" data-testid={`req-approval-${req.id}`}>
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={!!req.approved}
                                onChange={(e) => {
                                  if (!e.target.checked) toggleApproval(req, false);
                                }}
                                readOnly={!req.approved}
                                className="w-4 h-4 rounded border-slate-300 text-green-600 focus:ring-green-500/30 cursor-pointer"
                                data-testid={`req-approved-check-${req.id}`}
                              />
                              <span className={`text-xs font-semibold ${req.approved ? "text-green-700" : "text-slate-500"}`}>
                                {req.approved ? "Approved" : "Not Approved"}
                              </span>
                            </label>
                            {req.approved ? (
                              <div className="flex items-center gap-2">
                                <ShieldCheck className="w-3.5 h-3.5 text-green-600" />
                                <span className="text-xs text-green-700 font-medium">{req.approved_by}</span>
                                {req.approved_at && (
                                  <span className="text-[10px] text-slate-400">
                                    {new Date(req.approved_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <select
                                value=""
                                onChange={(e) => { if (e.target.value) toggleApproval(req, true, e.target.value); }}
                                className="h-7 px-2 rounded-md text-xs font-medium border border-slate-200 bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-400 cursor-pointer"
                                data-testid={`req-approver-select-${req.id}`}
                              >
                                <option value="">Select approver...</option>
                                {stakeholders.map((s) => (
                                  <option key={s.id} value={s.name}>{s.name}</option>
                                ))}
                              </select>
                            )}
                          </div>

                          {/* Blocked Reason - Only visible when status is Blocked */}
                          {req.status === "Blocked" && (
                            <div className="mt-3 pt-3 border-t border-red-100 pl-6" data-testid={`req-blocked-reason-${req.id}`}>
                              <div className="flex items-center gap-2 mb-2">
                                <AlertTriangle className="w-4 h-4 text-red-500" />
                                <span className="text-xs font-semibold text-red-700">Blocked Reason</span>
                              </div>
                              <Textarea
                                placeholder="Explain why this requirement is blocked..."
                                value={req.blocked_reason || ""}
                                onChange={(e) => updateBlockedReason(req, e.target.value)}
                                className="text-sm border-red-200 focus:border-red-400 focus:ring-red-500/30 bg-red-50/50"
                                rows={2}
                              />
                            </div>
                          )}

                          {/* Images Section */}
                          <div className="mt-3 pt-3 border-t border-slate-100 pl-6">
                            <div className="flex items-center justify-between">
                              <button
                                onClick={() => toggleImages(req.id)}
                                className="flex items-center gap-2 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
                              >
                                <Images className="w-4 h-4" />
                                <span>Attachments {(req.images || []).length > 0 ? `(${req.images.length})` : ""}</span>
                                {showImages[req.id] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                              </button>
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.tif,.webp,.svg,.heic,.heif"
                                  className="hidden"
                                  ref={(el) => (imageInputRefs.current[req.id] = el)}
                                  onChange={(e) => uploadImage(req.id, e.target.files?.[0])}
                                />
                                <span className="flex items-center gap-1.5 text-xs font-medium text-purple-600 hover:text-purple-700">
                                  {uploadingImage[req.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                                  {uploadingImage[req.id] ? "Uploading..." : "Add File"}
                                </span>
                              </label>
                            </div>
                            {showImages[req.id] && (req.images || []).length > 0 && (
                              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {(req.images || []).map((img, idx) => (
                                  <div key={idx} className="relative group">
                                    {img.format === 'pdf' || img.resource_type === 'raw' ? (
                                      <div className="w-full h-32 flex flex-col items-center justify-center bg-slate-100 rounded-lg border border-slate-200">
                                        <span className="text-2xl">📄</span>
                                        <span className="text-xs text-slate-500 mt-1">PDF</span>
                                      </div>
                                    ) : (
                                      <img src={img.url} alt={`Attachment ${idx + 1}`} className="w-full h-32 object-cover rounded-lg border border-slate-200" />
                                    )}
                                    <button
                                      onClick={() => deleteImage(req.id, idx)}
                                      className="absolute top-1 right-1 p-1 rounded bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                      title="Delete"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                    <a href={img.url} target="_blank" rel="noopener noreferrer" className="absolute bottom-1 left-1 text-[10px] bg-black/50 text-white px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity">View</a>
                                  </div>
                                ))}
                              </div>
                            )}
                            {showImages[req.id] && (req.images || []).length === 0 && (
                              <p className="mt-2 text-xs text-slate-400">No attachments yet. Click "Add File" to upload.</p>
                            )}
                          </div>

                          {/* Comments Section */}
                          <div className="mt-3 pt-3 border-t border-slate-100 pl-6">
                            <button
                              onClick={() => toggleComments(req.id)}
                              className="flex items-center gap-2 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
                              data-testid={`req-toggle-comments-${req.id}`}
                            >
                              <MessageSquare className="w-4 h-4" />
                              <span>Comments {comments[req.id]?.length > 0 ? `(${comments[req.id].length})` : ""}</span>
                              {showComments[req.id] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                            </button>

                            {showComments[req.id] && (
                              <div className="mt-3 space-y-3">
                                {loadingComments[req.id] ? (
                                  <div className="flex items-center gap-2 text-xs text-slate-400">
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Loading comments...
                                  </div>
                                ) : (
                                  <>
                                    {/* Existing Comments */}
                                    {(comments[req.id] || []).map((c) => (
                                      <div key={c.id} className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                                        <div className="flex items-start justify-between gap-2">
                                          <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                              <span className="text-xs font-semibold text-slate-700">{c.author_name}</span>
                                              <span className="text-[10px] text-slate-400">{formatDate(c.created_at)}</span>
                                            </div>
                                            <p className="text-sm text-slate-600 whitespace-pre-wrap">{c.comment}</p>
                                          </div>
                                          <button
                                            onClick={() => deleteComment(req.id, c.id)}
                                            className="p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors flex-shrink-0"
                                            title="Delete comment"
                                          >
                                            <Trash2 className="w-3 h-3" />
                                          </button>
                                        </div>
                                      </div>
                                    ))}

                                    {/* Add New Comment */}
                                    <div className="bg-white rounded-lg border border-slate-200 p-3">
                                      <select
                                        value={commentAuthor[req.id] || ""}
                                        onChange={(e) => setCommentAuthor((p) => ({ ...p, [req.id]: e.target.value }))}
                                        className="w-full mb-2 h-8 text-xs px-2 rounded-md border border-slate-200 bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400"
                                      >
                                        <option value="">Select your name...</option>
                                        {stakeholders.map((s) => (
                                          <option key={s.id} value={s.name}>{s.name}</option>
                                        ))}
                                      </select>
                                      <div className="flex gap-2">
                                        <Textarea
                                          placeholder="Add a comment..."
                                          value={newComment[req.id] || ""}
                                          onChange={(e) => setNewComment((p) => ({ ...p, [req.id]: e.target.value }))}
                                          className="flex-1 text-sm"
                                          rows={2}
                                        />
                                        <Button
                                          onClick={() => addComment(req.id)}
                                          disabled={!newComment[req.id]?.trim() || !commentAuthor[req.id]}
                                          className="h-auto px-3 bg-purple-600 hover:bg-purple-700 text-white"
                                        >
                                          <Send className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {filtered.length === 0 && !loading && (
            <div className="text-center py-12 text-slate-400" data-testid="no-requirements">
              <Circle className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">{requirements.length > 0 ? "No requirements match filters." : "No requirements yet. Add one to get started."}</p>
            </div>
          )}
        </div>
        <Pagination currentPage={reqPage} totalItems={filtered.length} pageSize={REQ_PAGE_SIZE} onPageChange={setReqPage} />
        </>
      )}

      {/* Add requirement */}
      {showForm ? (
        <Card className="mt-6 border border-slate-200 shadow-sm bg-white" data-testid="add-requirement-form">
          <CardContent className="p-6">
            <h3 className="text-sm font-semibold text-slate-700 font-['Outfit'] mb-4">Add Requirement</h3>
            <div className="space-y-3">
              <Input
                placeholder="Requirement title"
                value={formData.title}
                onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
                data-testid="req-title-input"
              />
              {/* Rich text toolbar for new requirement */}
              <div className="flex items-center gap-1 border border-slate-200 rounded-lg px-2 py-1 bg-slate-50">
                <button type="button" onMouseDown={(e) => { e.preventDefault(); execNew("bold"); }} className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Bold"><Bold className="w-4 h-4" /></button>
                <button type="button" onMouseDown={(e) => { e.preventDefault(); execNew("italic"); }} className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Italic"><Italic className="w-4 h-4" /></button>
                <div className="w-px h-5 bg-slate-200 mx-1" />
                <button type="button" onMouseDown={(e) => { e.preventDefault(); execNew("insertUnorderedList"); }} className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Bullets"><List className="w-4 h-4" /></button>
                <button type="button" onMouseDown={(e) => { e.preventDefault(); execNew("insertOrderedList"); }} className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Numbered List"><ListOrdered className="w-4 h-4" /></button>
                <div className="w-px h-5 bg-slate-200 mx-1" />
                <button type="button" onMouseDown={(e) => { e.preventDefault(); execNew("fontSize", "3"); }} className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Small"><span className="text-[10px] font-bold">A</span></button>
                <button type="button" onMouseDown={(e) => { e.preventDefault(); execNew("fontSize", "4"); }} className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Normal"><span className="text-xs font-bold">A</span></button>
                <button type="button" onMouseDown={(e) => { e.preventDefault(); execNew("fontSize", "5"); }} className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Large"><span className="text-sm font-bold">A</span></button>
                <button type="button" onMouseDown={(e) => { e.preventDefault(); execNew("fontSize", "6"); }} className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Extra Large"><span className="text-base font-bold">A</span></button>
                <div className="w-px h-5 bg-slate-200 mx-1" />
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Insert Table" data-testid="req-new-insert-table"><Table className="w-4 h-4" /></button>
                  </PopoverTrigger>
                  <PopoverContent className="w-48 p-3" align="start">
                    <p className="text-xs font-semibold text-slate-600 mb-2">Insert Table</p>
                    <div className="flex items-center gap-2 mb-2">
                      <label className="text-[10px] text-slate-500 w-10">Rows</label>
                      <Input type="number" min="2" max="20" defaultValue="3" className="h-7 text-xs" id="tbl-rows-new" />
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <label className="text-[10px] text-slate-500 w-10">Cols</label>
                      <Input type="number" min="1" max="10" defaultValue="3" className="h-7 text-xs" id="tbl-cols-new" />
                    </div>
                    <Button size="sm" className="w-full h-7 text-xs bg-[#0F172A] hover:bg-slate-800 text-white" onClick={() => { const r = parseInt(document.getElementById("tbl-rows-new").value) || 3; const c = parseInt(document.getElementById("tbl-cols-new").value) || 3; insertTable(null, r, c); }}>Insert</Button>
                  </PopoverContent>
                </Popover>
              </div>
              {/* Content editable description */}
              <div
                ref={newDescRef}
                contentEditable
                suppressContentEditableWarning
                data-testid="req-description-input"
                data-placeholder="Description (optional)"
                className="min-h-[80px] p-3 border border-slate-200 rounded-lg text-sm text-slate-700 leading-relaxed focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400 prose prose-sm max-w-none
                  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2
                  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2
                  [&_li]:my-0.5
                  [&_b]:font-bold [&_strong]:font-bold
                  [&_i]:italic [&_em]:italic
                  [&_table]:w-full [&_table]:border-collapse [&_table]:my-2
                  [&_th]:border [&_th]:border-slate-300 [&_th]:bg-slate-100 [&_th]:px-2.5 [&_th]:py-1.5 [&_th]:text-left [&_th]:text-xs [&_th]:font-semibold
                  [&_td]:border [&_td]:border-slate-300 [&_td]:px-2.5 [&_td]:py-1.5 [&_td]:text-xs
                  empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400"
              />
              <div className="flex items-center gap-3">
                <Select
                  value={formData.priority}
                  onValueChange={(val) => setFormData((p) => ({ ...p, priority: val }))}
                >
                  <SelectTrigger className="w-40 h-9" data-testid="req-priority-select">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="P0">P0 - Critical</SelectItem>
                    <SelectItem value="P1">P1 - High</SelectItem>
                    <SelectItem value="P2">P2 - Medium</SelectItem>
                    <SelectItem value="P3">P3 - Low</SelectItem>
                  </SelectContent>
                </Select>
                <select
                  value=""
                  onChange={(e) => {
                    const val = e.target.value;
                    if (!val) return;
                    if (!formData.assigned_to.includes(val)) {
                      setFormData((p) => ({ ...p, assigned_to: [...p.assigned_to, val] }));
                    }
                  }}
                  className="h-9 px-3 rounded-md text-sm border border-slate-200 bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400 cursor-pointer w-48"
                  data-testid="req-assigned-input"
                >
                  <option value="">Add assignee...</option>
                  {stakeholders.filter(s => !formData.assigned_to.includes(s.name)).map((s) => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                </select>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      className="h-9 px-3 rounded-md text-sm border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 flex items-center gap-2"
                      data-testid="req-duedate-input"
                    >
                      <CalendarDays className="w-4 h-4 text-slate-400" />
                      {formData.due_date ? 
                        (() => { try { return "Due: " + format(parseISO(formData.due_date), "MMM d, yyyy"); } catch { return formData.due_date; } })()
                        : "Due Date"}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={(() => { try { return formData.due_date ? parseISO(formData.due_date) : undefined; } catch { return undefined; } })()}
                      onSelect={(date) => setFormData((p) => ({ ...p, due_date: date ? format(date, "yyyy-MM-dd") : "" }))}
                    />
                  </PopoverContent>
                </Popover>
                {/* Pain Point Link */}
                <Select value={formData.pain_point_id || "_none"} onValueChange={(v) => setFormData((p) => ({ ...p, pain_point_id: v === "_none" ? "" : v }))}>
                  <SelectTrigger className="h-9 w-[80px] text-sm border-slate-200 bg-white text-slate-600" data-testid="req-painpoint-input">
                    <SelectValue placeholder="Pain Point" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">--</SelectItem>
                    {painPoints.map((pp, i) => (
                      <SelectItem key={pp.id} value={pp.id}>PP-{String(pp.pp_number || (i + 1)).padStart(3, '0')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Assigned chips */}
              {formData.assigned_to.length > 0 && (
                <div className="flex items-center gap-1 flex-wrap">
                  {formData.assigned_to.map((name, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full text-xs font-medium border border-purple-200">
                      {name}
                      <button
                        onClick={() => setFormData((p) => ({ ...p, assigned_to: p.assigned_to.filter(n => n !== name) }))}
                        className="hover:text-red-500 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2">
                <Button
                  onClick={addRequirement}
                  disabled={submitting || !formData.title.trim()}
                  className="bg-[#0F172A] hover:bg-slate-800 text-white"
                  data-testid="req-submit-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {submitting ? "Adding..." : "Add Requirement"}
                </Button>
                <Button variant="outline" onClick={() => { setShowForm(false); setFormData({ title: "", description: "", priority: "P1", assigned_to: [], due_date: "" }); }}>
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Button
          onClick={() => setShowForm(true)}
          className="mt-6 bg-[#0F172A] hover:bg-slate-800 text-white shadow-sm"
          data-testid="add-requirement-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Requirement
        </Button>
      )}
    </div>
    <ConfirmDeleteDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={() => deleteRequirement(deleteTarget?.id)} title="Delete Requirement" description={`Are you sure you want to delete "${deleteTarget?.label}"? This action cannot be undone.`} />
    </>
  );
};